// ShowcasePage - Blop language overview and feature demonstration
import { highlightCode } from '../highlight.js'

def CodeBlock(ctx: Component): VNode {
  { code, title, language } = ctx.attributes
  { value as highlightedHtml, setState as setHighlightedHtml } = ctx.state<string | null>('html', null)

  borderRadius = '6px'
  if title {
    borderRadius := '0 0 6px 6px'
  }

  lang = language || 'blop'
  ctx.mount(async () => {
    html = await highlightCode(code, lang, borderRadius)
    setHighlightedHtml(html)
  })

  <div style={ marginTop: '1em' }>
    if title {
      <div style={
          backgroundColor: '#2d3748',
          color: '#e2e8f0',
          padding: '0.5em 1em',
          fontWeight: '600',
          borderRadius: '6px 6px 0 0',
          fontSize: '0.85em',
          letterSpacing: '0.02em'
      }>
        = title
      </div>
    }
    if highlightedHtml {
      <div innerHTML=highlightedHtml />
    } else {
      <pre style={
          backgroundColor: '#1a202c',
          color: '#e2e8f0',
          padding: '1.25em 1.5em',
          borderRadius,
          overflow: 'auto',
          margin: '0',
          fontSize: '0.875em',
          lineHeight: '1.65'
      }><code>code</code></pre>
    }
  </div>
}

def CounterDemo(ctx: Component): VNode {
  { value as count, setState } = ctx.state<number>('count', 0)

  <div style={ textAlign: 'center', padding: '2em' }>
    <div style={ fontSize: '3.5em', fontWeight: 'bold', color: '#0d6efd', lineHeight: '1' }>count</div>
    <div style={ display: 'flex', gap: '0.75em', justifyContent: 'center', marginTop: '1.5em', flexWrap: 'wrap' }>
      <button
        class="btn btn-primary"
        on={ click: () => setState(count + 1) }>
        'Increment'
      </button>
      <button
        class="btn btn-outline-secondary"
        on={ click: () => setState(count - 1) }>
        'Decrement'
      </button>
      <button
        class="btn btn-outline-secondary"
        on={ click: () => setState(0) }>
        'Reset'
      </button>
    </div>
  </div>
}

def ListDemo(ctx: Component): VNode {
  { value as items, setState } = ctx.state<string[]>('items', ['Apples', 'Bananas', 'Cherries'])

  def addItem() {
    n = items.length + 1
    setState([...items, 'Item 'n])
  }

  def removeItem(index: number) {
    return () => {
      setState(items.filter((_, i) => i != index))
    }
  }

  <div style={ padding: '1em' }>
    <div style={ marginBottom: '1em' }>
      <button class="btn btn-sm btn-outline-primary" on={ click: addItem }>
        'Add item'
      </button>
    </div>

    <ul class="list-group">
      for index, item of items {
        <li class="list-group-item d-flex justify-content-between align-items-center">
          <span>
            <span style={ color: '#6c757d', fontSize: '0.85em', marginRight: '0.5em' }>'#'(index + 1)</span>
            = item
          </span>
          <button
            class="btn btn-sm btn-outline-danger"
            on={ click: removeItem(index) }>
            'Remove'
          </button>
        </li>
      }
    </ul>

    if items.length == 0 {
      <p class="text-muted text-center" style={ marginTop: '1em', fontSize: '0.95em' }>
        'List is empty.'
      </p>
    }
  </div>
}

def ConditionalDemo(ctx: Component): VNode {
  { value as status, setState as setStatus } = ctx.state<string>('status', 'online')
  { value as showDetails, setState as setShowDetails } = ctx.state<boolean>('showDetails', false)

  statuses = ['online', 'degraded', 'offline']

  dotColor = '#6c757d'
  cardBorder = '#dee2e6'
  statusText = 'Unknown'
  statusColor = '#6c757d'
  bgColor = '#f8f9fa'
  if status == 'online' {
    dotColor := '#198754'
    cardBorder := '#a3cfbb'
    statusText := 'All systems operational'
    statusColor := '#146c43'
    bgColor := '#d1e7dd'
  } elseif status == 'degraded' {
    dotColor := '#fd7e14'
    cardBorder := '#fecba1'
    statusText := 'Partial outage detected'
    statusColor := '#984c0c'
    bgColor := '#ffe5d0'
  } else {
    dotColor := '#dc3545'
    cardBorder := '#f1aeb5'
    statusText := 'Major outage in progress'
    statusColor := '#842029'
    bgColor := '#f8d7da'
  }

  <div style={ padding: '1em', maxWidth: '360px' }>
    // Status card
    <div style={ border: '1px solid 'cardBorder, borderRadius: '8px', overflow: 'hidden',
        backgroundColor: bgColor, marginBottom: '1em', transition: 'all 0.2s' }>
      <div style={ padding: '1em 1.25em', display: 'flex', alignItems: 'center', gap: '0.75em' }>
        <div style={ width: '12px', height: '12px', borderRadius: '50%',
            backgroundColor: dotColor, flexShrink: '0' } />
        <div>
          <div style={ fontWeight: '600', fontSize: '0.95em', color: statusColor }>
            = statusText
          </div>
          <div style={ fontSize: '0.8em', color: statusColor, opacity: '0.8', marginTop: '0.15em' }>
            if status == 'online' {
              'Last checked: just now'
            } elseif status == 'degraded' {
              'Investigating since 14:32'
            } else {
              'Incident started at 13:55'
            }
          </div>
        </div>
      </div>

      if showDetails {
        <div style={ borderTop: '1px solid 'cardBorder, padding: '0.75em 1.25em',
            fontSize: '0.82em', color: statusColor }>
          services = ['API', 'Database', 'CDN', 'Auth']
          for svc in services {
            svcOk = status == 'online' || (status == 'degraded' && svc != 'CDN')
            <div style={ display: 'flex', justifyContent: 'space-between',
                padding: '0.2em 0', borderBottom: '1px solid 'cardBorder, opacity: '0.9' }>
              <span>svc</span>
              <span style={ color: if svcOk => '#146c43' else '#842029', fontWeight: '600' }>
                = if svcOk => 'Operational' else 'Down'
              </span>
            </div>
          }
        </div>
      }
    </div>

    // Controls
    <div style={ display: 'flex', gap: '0.4em', flexWrap: 'wrap', marginBottom: '0.75em' }>
      for s in statuses {
        isActive = s == status
        btnStyle = if isActive => 'btn btn-sm btn-secondary' else 'btn btn-sm btn-outline-secondary'
        <button class=btnStyle on={ click: () => setStatus(s) }>s</button>
      }
    </div>
    <button class="btn btn-sm btn-outline-secondary" on={ click: () => setShowDetails(!showDetails) }>
      = if showDetails => 'Hide details' else 'Show service details'
    </button>
  </div>
}

def ShowcasePage(ctx: Component): VNode {
  { attributes } = ctx
  { _state } = attributes
  { value as activeTab, setState as setActiveTab } = ctx.state<string>('activeTab', 'overview')

  def switchTab(tab: string) {
    return (e) => {
      e.preventDefault()
      setActiveTab(tab)
    }
  }

  <div>
    // Header
    <div style={
        borderBottom: '1px solid #dee2e6',
        padding: '2.5em 2em 2em',
        backgroundColor: '#fff'
    }>
      <h1 style={ fontWeight: '700', marginBottom: '0.35em', fontSize: '2em' }>
        'Blop'
      </h1>
      <p style={ color: '#495057', fontSize: '1.05em', maxWidth: '700px', marginBottom: '1em', lineHeight: '1.5' }>
        'A typed language for the web that compiles to Virtual DOM. Blop uses real control flow statements — for loops, if/else — directly in templates, without JSX constraints.'
      </p>
      <div style={ display: 'flex', gap: '0.5em', flexWrap: 'wrap' }>
        <a
          href="https://github.com/batiste/blop-language"
          class="btn btn-sm btn-dark"
          target="_blank">
          'GitHub'
        </a>
        <a
          href="https://github.com/batiste/blop-language/blob/master/docs/QUICK_START.md"
          class="btn btn-sm btn-outline-secondary"
          target="_blank">
          'Quick Start'
        </a>
      </div>
    </div>

    // Navigation Tabs
    <ul class="nav nav-tabs" style={ backgroundColor: '#f8f9fa', paddingLeft: '2em', paddingTop: '0.5em',
        borderBottom: '1px solid #dee2e6' }>
      tabs = [
      { id: 'overview', label: 'Overview' },
{ id: 'counter', label: 'State' },
{ id: 'loops', label: 'For loops' },
{ id: 'conditionals', label: 'Conditionals' },
{ id: 'types', label: 'Types' },
{ id: 'comparison', label: 'Blop vs JSX' }
      ]

      for tab in tabs {
        isActive = tab.id == activeTab
        linkClass = 'nav-link'
        if isActive {
          linkClass := 'nav-link active'
        }
        <li class="nav-item">
          <a href='#'tab.id class=linkClass on={ click: switchTab(tab.id) } style={ cursor: 'pointer' }>
            = tab.label
          </a>
        </li>
      }
    </ul>

    // Tab Content
    <div style={ padding: '2em', backgroundColor: 'white', minHeight: '500px' }>
      if activeTab == 'overview' {
        <div key="overview" style={ maxWidth: '1100px' }>
          <h2 style={ fontSize: '1.25em', fontWeight: '600', marginBottom: '1.5em', color: '#212529' }>
            'How Blop differs from JSX-based frameworks'
          </h2>

          <p style={ color: '#495057', lineHeight: '1.7', marginBottom: '1.5em' }>
            'JSX is an expression language, which forces you to use .map() for iteration, ternary operators for conditionals, and IIFEs for local variables. '
            <strong>'Blop templates can do statements and expressions.'</strong>
          </p>

          <div class="row" style={ marginBottom: '2em' }>
            <div class="col-md-6" style={ marginBottom: '1.5em' }>
              <CodeBlock
                title="Blop"
                code="def UserList({ attributes }: Component): VNode {
  <ul>
    for user in attributes.users {
      isAdmin = user.role == 'admin'
      <li key=user.id> // statement
        = user.name
        // expression
        = if isAdmin => <span class=\"badge\">'admin'</span>
      </li>
    }
  </ul>
}" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1.5em' }>
              <CodeBlock
                title="Equivalent React / JSX"
                language="jsx"
                code="function UserList({ users }) {
  return (
    <ul>
      {users.map(user => {
        const isAdmin = user.role === 'admin';
        return (
          <li key={user.id}>
            {user.name}
            {isAdmin && (
              <span className=\"badge\">admin</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}" />
            </div>
          </div>

          <h3 style={ fontSize: '1.05em', fontWeight: '600', marginBottom: '1em', color: '#212529' }>
            'Key properties'
          </h3>
          <ul style={ color: '#495057', lineHeight: '2', paddingLeft: '1.5em' }>
            <li>
              <strong>'Flexible templates'</strong>
              ': for, if/elseif/else, and variable declarations are valid anywhere'
            </li>
            <li>
              <strong>'Compiled to Virtual DOM'</strong>
              ' via Snabbdom. Bundle size ~15 KB gzipped (vs. ~45 KB for React + ReactDOM)'
            </li>
            <li>
              <strong>'Optional type annotations'</strong>
              ' with inference. Generics are supported.'</li>
            <li>
              <strong>'No return statement'</strong>
              ': The top-level HTML node in a component is its output'</li>
            <li>
              <strong>'Built-in reactivity'</strong>
              ' via ctx.state() with typed initial values'</li>
            <li>
              <strong>'Vite integration'</strong>
              ' with HMR and source maps'</li>
          </ul>

          <div style={ marginTop: '2em', padding: '1.25em 1.5em', backgroundColor: '#f8f9fa', borderRadius: '6px',
              borderLeft: '3px solid #6c757d' }>
            <p style={ marginBottom: '0', color: '#495057', fontSize: '0.95em', lineHeight: '1.6' }>
              'Blop is not a drop-in replacement for React or a production alternative to established frameworks. It is a research project exploring what a web templating language looks like when designed around statements rather than expressions.'
            </p>
          </div>
        </div>
      } elseif activeTab == 'counter' {
        <div key="counter">
          <h2 style={ fontSize: '1.25em', fontWeight: '600', marginBottom: '0.5em', color: '#212529' }>
            'Reactive state'
          </h2>
          <p style={ color: '#6c757d', marginBottom: '2em', fontSize: '0.95em' }>
            'ctx.state<Type>(key, initialValue) returns a typed value and a setter. The component re-renders when the setter is called.'
          </p>

          <div class="row">
            <div class="col-lg-6" style={ marginBottom: '1.5em' }>
              <CodeBlock
                title="CounterDemo.blop"
                code="def CounterDemo(ctx: Component): VNode {
  { value as count, setState } = ctx.state<number>('count', 0)

  <div style={ textAlign: 'center', padding: '2em' }>
    <div style={ fontSize: '3.5em', fontWeight: 'bold' }>count</div>
    <div style={ display: 'flex', gap: '1em', justifyContent: 'center', marginTop: '1.5em' }>
      <button class=\"btn btn-primary\"
        on={ click: () => setState(count + 1) }>
        'Increment'
      </button>
      <button class=\"btn btn-outline-secondary\"
        on={ click: () => setState(count - 1) }>
        'Decrement'
      </button>
      <button class=\"btn btn-outline-secondary\"
        on={ click: () => setState(0) }>
        'Reset'
      </button>
    </div>
  </div>
}" />
            </div>

            <div class="col-lg-6">
              <div class="card">
                <div class="card-header" style={ fontSize: '0.85em', color: '#6c757d' }>'Live output'</div>
                <div class="card-body">
                  <CounterDemo />
                </div>
              </div>
            </div>
          </div>
        </div>
      } elseif activeTab == 'loops' {
        <div key="loops">
          <h2 style={ fontSize: '1.25em', fontWeight: '600', marginBottom: '0.5em', color: '#212529' }>
            'For loops in templates'
          </h2>
          <p style={ color: '#6c757d', marginBottom: '2em', fontSize: '0.95em' }>
            'Blop supports for item in array and for index, item of array directly inside markup. No callback required.'
          </p>

          <div class="row">
            <div class="col-lg-6" style={ marginBottom: '1.5em' }>
              <CodeBlock
                title="ListDemo.blop"
                code="def ListDemo(ctx: Component): VNode {
  { value as items, setState } = ctx.state<string[]>(
    'items', ['Apples', 'Bananas', 'Cherries'])

  def addItem() {
    n = items.length + 1
    setState([...items, 'Item ' + n])
  }

  def removeItem(index: number) {
    return () => setState(items.filter((_, i) => i != index))
  }

  <div>
    <button class=\"btn btn-sm btn-outline-primary\"
      on={ click: addItem }>
      'Add item'
    </button>

    <ul class=\"list-group\" style={ marginTop: '1em' }>
      for index, item of items {
        <li class=\"list-group-item d-flex justify-content-between\">
          = item
          <button on={ click: removeItem(index) }>'Remove'</button>
        </li>
      }
    </ul>
  </div>
}" />
            </div>

            <div class="col-lg-6">
              <div class="card">
                <div class="card-header" style={ fontSize: '0.85em', color: '#6c757d' }>'Live output'</div>
                <div class="card-body">
                  <ListDemo />
                </div>
              </div>
            </div>
          </div>
        </div>
      } elseif activeTab == 'conditionals' {
        <div key="conditionals">
          <h2 style={ fontSize: '1.25em', fontWeight: '600', marginBottom: '0.5em', color: '#212529' }>
            'Conditional rendering'
          </h2>
          <p style={ color: '#6c757d', marginBottom: '2em', fontSize: '0.95em' }>
            'Use if, elseif, and else directly in templates. Variables declared inside a block are scoped to that block.'
          </p>

          <div class="row">
            <div class="col-lg-6" style={ marginBottom: '1.5em' }>
              <CodeBlock
                title="ConditionalDemo.blop"
                code="def ConditionalDemo(ctx: Component): VNode {
  { value as status, setState as setStatus } =
    ctx.state<string>('status', 'online')
  { value as showDetails, setState as setShowDetails } =
    ctx.state<boolean>('showDetails', false)

  // Derive display values from current status
  dotColor = '#6c757d'
  statusText = 'Unknown'
  if status == 'online' {
    dotColor := '#198754'
    statusText := 'All systems operational'
  } elseif status == 'degraded' {
    dotColor := '#fd7e14'
    statusText := 'Partial outage detected'
  } else {
    dotColor := '#dc3545'
    statusText := 'Major outage in progress'
  }

  <div>
    <div style={ display: 'flex', alignItems: 'center', gap: '0.75em' }>
      <div style={ backgroundColor: dotColor, borderRadius: '50%',
          width: '12px', height: '12px' } />
      <strong>statusText</strong>
    </div>

    if showDetails {
      services = ['API', 'Database', 'CDN', 'Auth']
      for svc in services {
        svcOk = status == 'online' || svc != 'CDN'
        <div>
          svc': 'if svcOk => 'Operational' else 'Down'
        </div>
      }
    }

    statuses = ['online', 'degraded', 'offline']
    for s in statuses {
      isActive = s == status
      cls = if isActive => 'btn btn-sm btn-secondary'
              else 'btn btn-sm btn-outline-secondary'
      <button class=cls on={ click: () => setStatus(s) }>s</button>
    }
    <button on={ click: () => setShowDetails(!showDetails) }>
      = if showDetails => 'Hide details' else 'Show details'
    </button>
  </div>
}" />
            </div>

            <div class="col-lg-6">
              <div class="card">
                <div class="card-header" style={ fontSize: '0.85em', color: '#6c757d' }>'Live output'</div>
                <div class="card-body">
                  <ConditionalDemo />
                </div>
              </div>
            </div>
          </div>
        </div>
      } elseif activeTab == 'comparison' {
        <div key="comparison" style={ maxWidth: '1100px' }>
          <h2 style={ fontSize: '1.25em', fontWeight: '600', marginBottom: '0.35em', color: '#212529' }>
            'Blop vs React / JSX — side by side'
          </h2>
          <p style={ color: '#6c757d', marginBottom: '2em', fontSize: '0.95em' }>
            'The same functionality written in both languages. The differences are structural, not cosmetic.'
          </p>

          // Iteration
          <h3 style={ fontSize: '1em', fontWeight: '600', color: '#495057', marginBottom: '1em',
              borderBottom: '1px solid #dee2e6', paddingBottom: '0.5em' }>
            'Iteration'
          </h3>
          <div class="row" style={ marginBottom: '2.5em' }>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <p style={ fontSize: '0.8em', color: '#198754', fontWeight: '600', marginBottom: '0.5em' }>'Blop'</p>
              <CodeBlock
                code="<ul>
  for index, item of items {
    <li key=index>
      <strong>'#'index</strong>
      ' 'item.name
    </li>
  }
</ul>

// Object iteration
for key, value in config {
  <div>key': 'value</div>
}" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <p style={ fontSize: '0.8em', color: '#6c757d', fontWeight: '600', marginBottom: '0.5em' }>'React / JSX'</p>
              <CodeBlock
                language="jsx"
                code="<ul>
  {items.map((item, index) => (
    <li key={index}>
      <strong>#{index}</strong>
      {' ' + item.name}
    </li>
  ))}
</ul>

// Object iteration
{Object.entries(config).map(([key, value]) => (
  <div key={key}>{key}: {value}</div>
))}" />
            </div>
          </div>

          // Conditionals and local variables
          <h3 style={ fontSize: '1em', fontWeight: '600', color: '#495057', marginBottom: '1em',
              borderBottom: '1px solid #dee2e6', paddingBottom: '0.5em' }>
            'Conditionals and local variables'
          </h3>
          <div class="row" style={ marginBottom: '2.5em' }>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <p style={ fontSize: '0.8em', color: '#198754', fontWeight: '600', marginBottom: '0.5em' }>'Blop'</p>
              <CodeBlock
                code="<div>
  userName = user?.name ?? 'Guest'
  isAdmin = user?.role == 'admin'

  <h1>'Hello, 'userName</h1>

  if isAdmin {
    <div class=\"admin-panel\">'Admin controls'</div>
  } elseif user?.role == 'moderator' {
    <div class=\"mod-panel\">'Moderator controls'</div>
  } else {
    <div class=\"user-panel\">'User controls'</div>
  }
</div>" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <p style={ fontSize: '0.8em', color: '#6c757d', fontWeight: '600', marginBottom: '0.5em' }>'React / JSX'</p>
              <CodeBlock
                language="jsx"
                code="<div>
  {(() => {
    const userName = user?.name ?? 'Guest';
    const isAdmin = user?.role === 'admin';
    return (
      <>
        <h1>Hello, {userName}</h1>
        {isAdmin ? (
          <div className=\"admin-panel\">Admin controls</div>
        ) : user?.role === 'moderator' ? (
          <div className=\"mod-panel\">Moderator controls</div>
        ) : (
          <div className=\"user-panel\">User controls</div>
        )}
      </>
    );
  })()}
</div>" />
            </div>
          </div>

          // Component definition
          <h3 style={ fontSize: '1em', fontWeight: '600', color: '#495057', marginBottom: '1em',
              borderBottom: '1px solid #dee2e6', paddingBottom: '0.5em' }>
            'Component definition'
          </h3>
          <div class="row" style={ marginBottom: '2.5em' }>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <p style={ fontSize: '0.8em', color: '#198754', fontWeight: '600', marginBottom: '0.5em' }>'Blop'</p>
              <CodeBlock
                code="def TodoItem(ctx: Component): VNode {
  { value, setState } = ctx.state('done', false)

  <li
    class=if done => 'done' else 'todo'
    on={ click: () => setState(!value) 
  >
    = ctx.attributes.todo.title
    = if done => <span class=\"check\">'checkmark'</span>
  </li>
}" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <p style={ fontSize: '0.8em', color: '#6c757d', fontWeight: '600', marginBottom: '0.5em' }>'React / JSX'</p>
              <CodeBlock
                language="jsx"
                code="function TodoItem({ todo }) {
  const [done, setDone] = useState(false);

  return (
    <li
      className={done ? 'done' : 'todo'}
      onClick={() => setDone(!done)}
    >
      {todo.title}
      {done && (
        <span className=\"check\">checkmark</span>
      )}
    </li>
  );
}" />
            </div>
          </div>

          <div style={ padding: '1.25em 1.5em', backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '0.9em',
              color: '#495057', lineHeight: '1.6' }>
            'These comparisons show the structural difference: Blop requires no expression-wrapping ceremony because its template parser understands statements. The tradeoff is that Blop is a new language with its own toolchain, while JSX runs on the widely-supported TypeScript/Babel ecosystem.'
          </div>
        </div>
      } elseif activeTab == 'types' {
        <div key="types" style={ maxWidth: '1100px' }>
          <h2 style={ fontSize: '1.25em', fontWeight: '600', marginBottom: '0.35em', color: '#212529' }>
            'Type system'
          </h2>
          <p style={ color: '#6c757d', marginBottom: '2em', fontSize: '0.95em' }>
            'Blop has structural, optional type annotations with inference. Types are erased at compile time: they exist only to catch mistakes in the editor and CLI. It is possible that in future versions, some type information may be preserved at runtime Python style.'
          </p>

          // Basic annotations
          <h3 style={ fontSize: '1em', fontWeight: '600', color: '#495057', marginBottom: '1em',
              borderBottom: '1px solid #dee2e6', paddingBottom: '0.5em' }>
            'Function annotations'
          </h3>
          <div class="row" style={ marginBottom: '2.5em' }>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="Annotated parameters and return type"
                code="def greet(name: string, age: number): string {
  return 'Hello 'name', you are 'age
}

// Annotations are optional — inference fills in the gaps
def add(a: number, b: number) {
  return a + b   // inferred as number
}" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="Type aliases and object shapes"
                code="type Point = { x: number, y: number }

def distance(a: Point, b: Point): number {
  dx = a.x - b.x
  dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

// Optional properties
type User = {
  name: string,
  email: string,
  avatar?: string    // may be absent
}" />
            </div>
          </div>

          // Union and literal types
          <h3 style={ fontSize: '1em', fontWeight: '600', color: '#495057', marginBottom: '1em',
              borderBottom: '1px solid #dee2e6', paddingBottom: '0.5em' }>
            'Union types and literal types'
          </h3>
          <div class="row" style={ marginBottom: '2.5em' }>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="String literal union"
                code="type Status = 'active' | 'pending' | 'inactive'
type Direction = 'north' | 'south' | 'east' | 'west'

def statusLabel(s: Status): string {
  if s == 'active'  { return 'Running' }
  if s == 'pending' { return 'Waiting' }
  return 'Stopped'
}

// Mixed primitive union
type Id = string | number" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="Type narrowing in if / else"
                code="type Result = { ok: true, data: string } | { ok: false, error: string }

def show(r: Result): string {
  if r.ok {
    // r.data is available here
    return 'Got: 'r.data
  } else {
    // r.error is available here
    return 'Error: 'r.error
  }
}" />
            </div>
          </div>

          // Generics
          <h3 style={ fontSize: '1em', fontWeight: '600', color: '#495057', marginBottom: '1em',
              borderBottom: '1px solid #dee2e6', paddingBottom: '0.5em' }>
            'Generics'
          </h3>
          <div class="row" style={ marginBottom: '2.5em' }>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="Generic functions"
                code="def first<T>(items: T[]): T | undefined {
  return items[0]
}

def wrap<T>(value: T): { value: T } {
  return { value }
}

// ctx.state uses the same mechanism
{ value as count, setState } =
  ctx.state('count', 0)" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="Generic type aliases and extends constraints"
                code="// A paginated API response
type Page<T> = {
  items: T[],
  total: number,
  page: number
}

// Constrained type parameter
type WithId<T extends { id: number }> = {
  item: T,
  index: number
}

type UserPage = Page<User>" />
            </div>
          </div>

          // Import / export
          <h3 style={ fontSize: '1em', fontWeight: '600', color: '#495057', marginBottom: '1em',
              borderBottom: '1px solid #dee2e6', paddingBottom: '0.5em' }>
            'Sharing types across files'
          </h3>
          <div class="row" style={ marginBottom: '2em' }>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="types.blop"
                code="// Top-level declarations are exported automatically
type User = {
  id: number,
  name: string,
  role: 'admin' | 'user'
}

type ApiResponse<T> = {
  data: T,
  status: number
}" />
            </div>
            <div class="col-md-6" style={ marginBottom: '1em' }>
              <CodeBlock
                title="profile.blop"
                code="import { User, ApiResponse } from './types.blop'

def fetchUser(id: number): ApiResponse<User> {
  // return type is checked against the alias
  return { data: { id, name: 'Alice', role: 'user' }, status: 200 }
}

def Profile({ attributes }: Component): VNode {
  user: User = attributes.user
  <div>
    <h2>user.name</h2>
    if user.role == 'admin' {
      <span class=\"badge\">'admin'</span>
    }
  </div>
}" />
            </div>
          </div>

          <div style={ padding: '1.25em 1.5em', backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '0.9em',
              color: '#495057', lineHeight: '1.6' }>
            'All type annotations are optional. The inference engine fills in types from usage, so you can start without annotations and add them progressively where they add the most clarity or catch the most bugs.'
          </div>
        </div>
      }
    </div>

    // Footer
    <div style={
        borderTop: '1px solid #dee2e6',
        padding: '1.5em 2em',
        backgroundColor: '#f8f9fa',
        color: '#6c757d',
        fontSize: '0.9em'
    }>
      <div style={ display: 'flex', gap: '1.5em', flexWrap: 'wrap', alignItems: 'center' }>
        <span>'Blop language'</span>
        <a href="https://github.com/batiste/blop-language" target="_blank" style={ color: '#495057' }>'Source on GitHub'</a>
        <a href="https://www.npmjs.com/package/blop-language" target="_blank" style={ color: '#495057' }>'npm package'</a>
        <span style={ marginLeft: 'auto' }>'~15 KB gzipped (with Snabbdom virtual DOM)'</span>
      </div>
    </div>

  </div>
}
